import { useState } from "react";
import { ACTIVE_ROW_CSS } from "Components/Patients/EditViewPatient/Forms/DetailSection/DetailSection";
import { useNotePdfView } from "CustomHooks/useNotePdfView";
import { useNotePdfInvalidate } from "CustomHooks/useNotePdfInvalidate";
import { InvalidatePdfModal } from "./InvalidatePdfModal";
// Patient Name and State are omitted: both tables render inside a single
// patient's detail page, where the patient is already the context and the
// state is shown on the visit itself. Dropping them buys width for the
// columns that vary per note.
// Thirteen columns did not fit without horizontal scrolling, and most of them
// carried a single badge. The three note dates now share one cell (they are
// read together anyway), the four state badges share another, and View Content
// moved in with the other row actions.
const TABLE_HEADINGS = [
  "Created At",
  "Note Dates",
  "Days Off",
  // The doctor on the note's VISIT — not necessarily whoever wrote the note.
  "Doctor",
  // Who actually issued it: a provider or an admin. Blank on notes issued before
  // authorship was recorded, which is deliberately shown rather than guessed.
  "Created By",
  "Note Type",
  // Only set when the patient told the note flow they work somewhere with its
  // own HR requirements (today: Amazon); blank for everyone else.
  "Company",
  "Delivery Time",
  "Status",
  "Actions",
];

/** Keep the empty-state colSpan in step with the column count. */
export const VISIT_PDFS_COLUMN_COUNT = TABLE_HEADINGS.length;

export const VisitPdfsTableHeading = () => {
  return TABLE_HEADINGS.map((head, index) => (
    <th key={index} scope="col" className="bali_hai px-3 py-3 text-left text-sm font-medium">
      {head}
    </th>
  ));
};

const NOTE_TYPE_STYLES = {
  rush: {
    bg: "bg-red-100",
    text: "text-red-800",
  },
  standard: {
    bg: "bg-blue-100",
    text: "text-blue-800",
  },
};

const NoteTypeBadge = ({ noteType }) => {
  if (!noteType) return <span className="text-sm text-gray-400">None</span>;
  const style = NOTE_TYPE_STYLES[noteType?.toLowerCase()] || {
    bg: "bg-gray-100",
    text: "text-gray-800",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>{noteType}</span>
  );
};

/**
 * Who issued the note. Notes predating authorship tracking carry no author at all —
 * shown as "Not recorded" rather than being attributed to the visit's doctor, which
 * would be a guess.
 */
const CreatedByCell = ({ createdByName, createdByType }) => {
  if (!createdByName) {
    return <span className="text-gray-400">Not recorded</span>;
  }
  const isAdmin = String(createdByType || "").toUpperCase() === "ADMIN";
  return (
    <span className="inline-flex items-center gap-1.5">
      {createdByName}
      {isAdmin && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          Admin
        </span>
      )}
    </span>
  );
};

const BooleanBadge = ({ value, trueLabel = "Yes", falseLabel = "No" }) => {
  if (value) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        {trueLabel}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">{falseLabel}</span>
  );
};

// The three note dates in one cell: the label carries the meaning the column
// header used to, so the value can stay on one line. Note dates are day-level
// (the backend always stamps midnight), so the "12:00am PST" the API tacks on
// is noise that only widened the column.
// Companies come back as the stored enum ("AMAZON"); shout-case in a data
// cell reads as an error state, so present it as a name.
const titleCase = (value) =>
  typeof value === "string" && value
    ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    : value;

const dayOnly = (value) =>
  typeof value === "string" ? value.replace(/\s+12:00am.*$/i, "") : value;

const DateLine = ({ label, value }) =>
  value ? (
    <div className="whitespace-nowrap">
      <span className="bali_hai">{label}: </span>
      {dayOnly(value)}
    </div>
  ) : null;

const StatusBadge = ({ pdfCreated }) => {
  if (pdfCreated) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Ready</span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Not Available</span>
  );
};

const ViewPdfAction = ({ pdfId, canView, onError }) => {
  const { viewPdf, isLoading } = useNotePdfView();

  if (!canView) {
    return (
      <span className="text-gray-400 cursor-not-allowed" title="PDF not available">
        View PDF
      </span>
    );
  }

  return (
    <span
      className={isLoading ? "opacity-50" : ""}
      onClick={() => {
        if (isLoading) return;
        viewPdf(pdfId, {
          onError: (err) => {
            const message = err?.response?.status === 404 ? "PDF not available for viewing" : err?.message || "Failed to load PDF";
            onError(message);
            if (process.env.NODE_ENV === "development") {
              console.error("[ViewPdf][ERROR]", err);
            }
          },
        });
      }}
    >
      {isLoading ? "Loading..." : "View PDF"}
    </span>
  );
};

/**
 * navigator.clipboard only exists in a secure context, so it is undefined for
 * anyone reaching the admin over plain HTTP (a LAN/Tailscale hostname, say) —
 * where calling it threw instead of copying. Fall back to the legacy
 * execCommand path, and report failure rather than dying.
 */
const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied or blocked — try the fallback below.
    }
  }
  try {
    const field = document.createElement("textarea");
    field.value = text;
    // Keep it off-screen and unfocusable-looking so the page does not jump.
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.top = "-1000px";
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(field);
    return ok;
  } catch {
    return false;
  }
};

const CopyReferenceIdAction = ({ documentReferenceId, onCopied, onError }) => {
  const [copied, setCopied] = useState(false);

  if (!documentReferenceId) {
    return <span className="text-gray-400">No Ref ID</span>;
  }

  return (
    <span
      className="cursor-pointer"
      onClick={async () => {
        const ok = await copyText(documentReferenceId);
        if (!ok) {
          onError?.("Could not copy — copy it manually: " + documentReferenceId);
          return;
        }
        setCopied(true);
        onCopied?.("Reference ID copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      {copied ? "Copied!" : "Copy Ref ID"}
    </span>
  );
};

const InvalidatePdfAction = ({ pdfId, visitId, disabled = false, patientId, onError, startDate, endDate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { invalidatePdf, isLoading } = useNotePdfInvalidate(visitId, patientId);

  if (disabled) {
    return (
      <span className="text-gray-400 cursor-not-allowed" title="PDF not available">
        Invalidate
      </span>
    );
  }
  return (
    <>
      <span
        className={`coral_red ${isLoading ? "opacity-50" : ""} cursor-pointer`}
        onClick={() => {
          if (!isLoading) setIsModalOpen(true);
        }}
      >
        {isLoading ? "Invalidating..." : "Invalidate"}
      </span>
      <InvalidatePdfModal
        isModalOpen={isModalOpen}
        isLoading={isLoading}
        startDate={startDate}
        endDate={endDate}
        onInvalidate={() => {
          invalidatePdf(pdfId, {
            onSuccess: () => setIsModalOpen(false),
            onError: (err) => {
              setIsModalOpen(false);
              onError(err?.message || "Failed to invalidate PDF");
            },
          });
        }}
        onCancel={() => setIsModalOpen(false)}
      />
    </>
  );
};

export const VisitPdfsTableBody = ({ data, visitId, patientId, onViewContent, onViewPdfError, onInvalidateError, onCopied, onEdit }) => {
  const [activeRowId, setActiveRowId] = useState(null);

  return data.map((pdf, index) => {
    const pdfId = pdf._id || pdf.id;
    const canView = pdf.pdfCreated;
    // The API now returns an explicit flag; the reference-id suffix is still checked
    // so notes issued before the flag existed keep rendering as invalidated.
    const isInValidated =
      pdf?.isInvalidated === true ||
      !!pdf?.documentReferenceId?.toUpperCase().includes("DEACTIVATED");
    return (
      <tr
        key={pdfId || index}
        className={(pdfId || index) === activeRowId ? ACTIVE_ROW_CSS : undefined}
      >
        {/* <td className="px-3 py-4 whitespace-nowrap text-sm sapphire">{formatRaw(pdf.createdAt)}</td> */}
        <td className="px-3 py-4 whitespace-nowrap text-sm sapphire">{pdf.createdAt}</td>

        <td className="px-3 py-4 text-sm sapphire">
          <DateLine label="Start" value={pdf.startDate} />
          <DateLine label="End" value={pdf.endDate} />
          <DateLine label="Return" value={pdf.returnDate} />
        </td>
        <td className="px-3 py-4 whitespace-nowrap text-sm sapphire">
          {pdf.daysOff != null ? pdf.daysOff : <span className="text-gray-400">None</span>}
        </td>
        <td className="px-3 py-4 whitespace-nowrap text-sm sapphire">
          {pdf.doctorName || <span className="text-gray-400">None</span>}
        </td>
        <td className="px-3 py-4 whitespace-nowrap text-sm sapphire">
          <CreatedByCell createdByName={pdf.createdByName} createdByType={pdf.createdByType} />
        </td>
        <td className="px-3 py-4 whitespace-nowrap text-sm">
          <NoteTypeBadge noteType={pdf.noteType} />
        </td>
        {/* Left blank, not "None": no employer is the norm here, so a
            placeholder on most rows would be pure noise. */}
        <td className="px-3 py-4 whitespace-nowrap text-sm sapphire">
          {pdf.company ? titleCase(pdf.company) : ""}
        </td>
        <td className="px-3 py-4 whitespace-nowrap text-sm sapphire">
          {pdf.deliveryTime || <span className="text-gray-400">None</span>}
        </td>
        <td className="px-3 py-4 text-sm">
          <div className="flex flex-wrap gap-1.5 max-w-[13rem]">
            <StatusBadge pdfCreated={pdf.pdfCreated} />
            <BooleanBadge value={pdf.freshdeskSent} trueLabel="Sent" falseLabel="Not Sent" />
            <BooleanBadge value={pdf.s3Stored} trueLabel="Stored" falseLabel="Not Stored" />
            <BooleanBadge value={pdf.skipChat} trueLabel="Skipped" falseLabel="No Skip" />
          </div>
        </td>
        <td
          className="px-3 py-4 whitespace-nowrap text-sm text-blue-chill cursor-pointer"
          onClick={() => setActiveRowId(pdfId || index)}
        >
          <div className="flex flex-col gap-2">
            <ViewPdfAction pdfId={pdfId} canView={canView} onError={onViewPdfError} />
            {pdf.content ? (
              <span onClick={() => onViewContent(pdf.content)}>View Content</span>
            ) : (
              <span className="text-gray-400">No Content</span>
            )}
            <CopyReferenceIdAction
              documentReferenceId={pdf.documentReferenceId}
              onCopied={onCopied}
              onError={onViewPdfError}
            />
            {/* An invalidated note is a closed record — reissue via Create instead. */}
            {onEdit && !isInValidated && (
              <span onClick={() => onEdit(pdf)}>Edit</span>
            )}
            <InvalidatePdfAction
              pdfId={pdfId}
              // Patient-wide list spans visits, so prefer the row's own
              // visit; the per-visit view passes only the prop.
              visitId={pdf.visitId ? String(pdf.visitId) : visitId}
              disabled={isInValidated}
              patientId={patientId}
              onError={onInvalidateError}
              startDate={pdf.startDate}
              endDate={pdf.endDate}
            />
          </div>
        </td>
      </tr>
    );
  });
};
